import {ID} from '@wandb/weave/common/util/id';
import * as queryString from 'query-string';
import {useCallback} from 'react';
import {useHistory} from 'react-router-dom';

import {
  useCreateViewCommentsAlertSubscriptionMutation,
  useDeleteAlertSubscriptionMutation,
  type ViewSource,
} from '../../generated/graphql';
import {propagateErrorsContext} from '../../util/errors';
import * as Urls from '../../util/urls';
import {openInNewTab} from '../../util/window';
import {useApolloClient} from '../hooks';
import {type ApolloClient} from '../types';
import {setCommentAlertSubscription} from '../views/actions';
import * as ViewApi from '../views/api';
import * as ViewHooks from '../views/hooks';
import type * as ReportTypes from '../views/report/types';
import type * as ViewTypes from '../views/types';
import {type PushReportOpts, type ReportViewRef} from './types';

export function usePushReport(
  entityName: string,
  projectName?: string,
  draft = true
) {
  const client = useApolloClient();
  const history = useHistory();

  return useCallback(
    (
      config: ReportTypes.ReportConfig,
      reportName?: string,
      opts?: PushReportOpts
    ) =>
      pushReport({
        client,
        entityName,
        projectName,
        reportName,
        draft,
        config,
        opts,
        history,
      }),
    [client, entityName, projectName, draft, history]
  );
}

export function usePushGalleryPost(entityName: string, reportName: string) {
  const client = useApolloClient();
  const history = useHistory();

  return useCallback(
    (config: ReportTypes.ReportConfig) =>
      pushReport({
        client,
        entityName,
        reportName,
        config,
        history,
      }),
    [client, entityName, reportName, history]
  );
}

interface SaveViewParams {
  client: ApolloClient;
  entityName: string;
  projectName?: string;
  reportName?: string | null;
  draft?: boolean;
  discussion?: boolean;
  config: ReportTypes.ReportConfig;
  createdUsing?: ViewSource;
  opts?: PushReportOpts;
}

interface PushReportParams extends SaveViewParams {
  history: ReturnType<typeof useHistory>;
}

export async function saveView({
  client,
  entityName,
  projectName,
  reportName,
  draft,
  config,
  createdUsing,
}: SaveViewParams) {
  return await ViewApi.save(
    client,
    {
      name: ID(12),
      displayName: reportName || 'Untitled Report',
      type: draft ? 'runs/draft' : 'runs',
      project: {
        entityName,
        name: projectName,
      },
      description: '',
      spec: config,
      createdUsing,
    },
    propagateErrorsContext()
  );
}

export async function pushReport({
  client,
  entityName,
  projectName,
  reportName,
  draft = true,
  discussion,
  config,
  opts,
  history,
}: PushReportParams) {
  const savedView = await saveView({
    client,
    entityName,
    projectName,
    reportName,
    draft,
    config,
  });
  if (savedView.id == null) {
    throw new Error('invalid state');
  }

  let redirectTo: string;
  if (projectName == null) {
    const urlParams = {
      entityName,
      reportID: savedView.id,
      reportName: savedView.displayName,
    };
    redirectTo = discussion
      ? draft
        ? Urls.galleryDiscussionEdit(urlParams)
        : Urls.galleryDiscussionView(urlParams)
      : draft
        ? Urls.galleryPostEdit(urlParams)
        : Urls.galleryPostView(urlParams);
  } else {
    const urlParams = {
      entityName,
      projectName,
      reportID: savedView.id,
      reportName: savedView.displayName,
    };
    redirectTo = draft
      ? Urls.reportEdit(urlParams, `?draftId=${savedView.id}`)
      : Urls.reportView(urlParams);
  }
  if (opts?.redirectQS != null) {
    redirectTo += draft ? '&' : '?';
    redirectTo += `${queryString.stringify(opts.redirectQS)}`;
  }

  if (opts?.newTab) {
    openInNewTab(redirectTo, history);
  } else {
    history.push(redirectTo);
  }
}

// Subscribe / unsubscribe for report comments
export function useReportCommentAlerts(
  viewRef: ReportViewRef,
  view: ViewTypes.LoadableView
) {
  const reportServerID = view.id;
  if (reportServerID == null) {
    throw new Error('invalid state');
  }

  const setAlertSubscription = ViewHooks.useViewAction(
    viewRef as ViewTypes.PartRefFromObjSchema<ReportTypes.ReportObjSchema>,
    setCommentAlertSubscription
  );

  const [createSubscriptionMutation] =
    useCreateViewCommentsAlertSubscriptionMutation();
  const subscribe = useCallback(
    () =>
      createSubscriptionMutation({
        variables: {viewID: reportServerID},
      }).then(response => {
        const subscriptionID =
          response.data?.createViewCommentsAlertSubscription?.subscription.id;
        if (subscriptionID != null) {
          setAlertSubscription(subscriptionID);
        }
      }),
    [createSubscriptionMutation, reportServerID, setAlertSubscription]
  );

  const [deleteSubscriptionMutation] = useDeleteAlertSubscriptionMutation();
  const existingSubscriptionID = view.alertSubscription?.id;
  const unsubscribe = useCallback(() => {
    if (existingSubscriptionID != null) {
      deleteSubscriptionMutation({
        variables: {id: existingSubscriptionID},
      }).then(response => {
        if (response.data?.deleteAlertSubscription?.success) {
          setAlertSubscription(undefined);
        }
      });
    }
  }, [
    existingSubscriptionID,
    deleteSubscriptionMutation,
    setAlertSubscription,
  ]);

  return {isSubscribed: view.alertSubscription != null, subscribe, unsubscribe};
}
